<template>
  <div class="inquiries-page">

    <!-- Access Denied -->
    <div v-if="!canViewInquiries" class="text-center pa-12">
      <v-icon x-large color="grey lighten-1">mdi-lock-outline</v-icon>
      <h2 class="mt-4 grey--text">Access Restricted</h2>
      <p class="grey--text">You do not have permission to view Inquiries. Please ask an admin to enable the <strong>View Inquiries</strong> permission for your account.</p>
    </div>

    <!-- Page Header -->
    <template v-if="canViewInquiries">
    <div class="inq-header">
      <div class="inq-header-left">
        <v-icon color="blue darken-3" class="mr-2">mdi-comment-question-outline</v-icon>
        <span>Inquiries</span>
        <v-chip class="ml-3" small :color="statusChipColor('Open')" dark>
          {{ openCount }} Open
        </v-chip>
      </div>
      <div class="inq-header-right">
        <v-btn color="blue darken-3 white--text py-2" dark small @click="openCreateDialog" class="mr-2">
          <v-icon left small>mdi-plus</v-icon> New Inquiry
        </v-btn>
        <v-btn icon small @click="loadInquiries" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Status Filter Tabs -->
    <div class="inq-filter-bar" style="display: flex; justify-content: space-between; align-items: center;">
      <v-btn-toggle v-model="statusFilter" mandatory dense>
        <v-btn small value="All">All ({{ allInquiries.length }})</v-btn>
        <v-btn small value="Open">
          <v-icon small left color="red">mdi-circle</v-icon>Open ({{ countByStatus('Open') }})
        </v-btn>
        <v-btn small value="In Progress">
          <v-icon small left color="orange">mdi-circle</v-icon>In Progress ({{ countByStatus('In Progress') }})
        </v-btn>
        <v-btn small value="Resolved">
          <v-icon small left color="green">mdi-circle</v-icon>Resolved ({{ countByStatus('Resolved') }})
        </v-btn>
        <v-btn small value="Closed">
          <v-icon small left color="grey">mdi-circle</v-icon>Closed ({{ countByStatus('Closed') }})
        </v-btn>
      </v-btn-toggle>

      <v-text-field
        v-model="searchQuery"
        append-icon="mdi-magnify"
        label="Search Inquiries..."
        single-line
        hide-details
        dense
        outlined
        style="max-width: 300px;"
      ></v-text-field>
    </div>

    <!-- Inquiries Table -->
    <div class="inq-table-wrap">
      <v-simple-table fixed-header>
        <thead>
          <tr>
            <th>#</th>
            <th>Customer</th>
            <th>Title</th>
            <th>Category</th>
            <th>Priority</th>
            <th>Attachment</th>
            <th>Status</th>
            <th>Created By</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="text-center pa-6">
              <v-progress-circular indeterminate color="blue darken-3 white--text py-2"></v-progress-circular>
            </td>
          </tr>
          <tr v-else-if="filteredInquiries.length === 0">
            <td colspan="9" class="text-center pa-6 grey--text">No inquiries found.</td>
          </tr>
          <tr v-for="(inq, idx) in filteredInquiries" :key="inq.id" class="inq-row">
            <td><strong>{{ filteredInquiries.length - idx }}</strong></td>
            <td>
              <span v-if="inq.customer">{{ inq.customer.name }}</span>
              <span v-else-if="inq.customerName">{{ inq.customerName }}</span>
              <span v-else class="grey--text">Walk-in</span>
            </td>
            <td>{{ inq.title }}</td>
            <td>
              <v-chip x-small outlined>{{ inq.category === 'Other' && inq.customCategory ? inq.customCategory : inq.category }}</v-chip>
            </td>
            <td>
              <v-chip x-small dark :color="priorityColor(inq.priority)">{{ inq.priority }}</v-chip>
            </td>
            <td>
              <template v-if="inq.attachments">
                <v-btn v-for="(path, i) in parseAttachments(inq.attachments)" :key="'att'+i" icon x-small :href="getAttachmentUrl(path)" target="_blank" title="View Attachment">
                  <v-icon small color="grey darken-2">mdi-paperclip</v-icon>
                </v-btn>
              </template>
              <template v-else-if="inq.attachmentPath">
                <v-btn icon x-small :href="getAttachmentUrl(inq.attachmentPath)" target="_blank" title="View Attachment">
                  <v-icon small color="grey darken-2">mdi-paperclip</v-icon>
                </v-btn>
              </template>
              <span v-else class="grey--text">—</span>
            </td>
            <td>
              <v-chip x-small dark :color="statusChipColor(inq.status)">{{ inq.status }}</v-chip>
            </td>
            <td>{{ inq.createdByUser ? inq.createdByUser.name : '—' }}</td>
            <td>{{ formatDate(inq.createdAt) }}</td>
            <td>
              <v-btn icon x-small @click="openDetailDialog(inq, filteredInquiries.length - idx)" title="View / Edit">
                <v-icon small color="blue darken-3">mdi-eye</v-icon>
              </v-btn>
              <v-btn icon x-small @click="confirmDelete(inq, filteredInquiries.length - idx)" title="Delete">
                <v-icon small color="red lighten-1">mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- ===== CREATE INQUIRY DIALOG ===== -->
    <v-dialog v-model="showCreateDialog" max-width="680px" persistent>
      <v-card>
        <v-card-title class="blue darken-3 white--text py-2">
          <v-icon dark left>mdi-comment-plus-outline</v-icon>
          New Inquiry
        </v-card-title>
        <v-container class="pt-4">
          <v-alert v-if="formError" type="error" dense dismissible @input="formError = null">{{ formError }}</v-alert>
          <v-row>
            <v-col cols="12">
              <v-text-field v-model="form.title" label="Title / Subject *" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.category"
                :items="categories"
                label="Category *"
                outlined dense hide-details
                @change="onCategoryChange"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" v-if="form.category === 'Other'">
              <v-text-field v-model="form.customCategory" label="Specify Category" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.priority"
                :items="priorities"
                label="Priority"
                outlined dense hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" v-if="form.category === 'Flight Booking' || form.category === 'Ticket Cancellation' || form.category === 'Ticket Refund'">
              <v-text-field v-model="form.flightStartDate" label="Flight Start Date" type="date" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" v-if="form.category === 'Flight Booking' || form.category === 'Ticket Cancellation' || form.category === 'Ticket Refund'">
              <v-text-field v-model="form.flightEndDate" label="Flight End Date" type="date" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="form.description" label="Description / Details" outlined dense rows="3" hide-details></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-file-input
                label="Add Attachment(s)"
                outlined dense hide-details
                prepend-icon="mdi-paperclip"
                multiple
                @change="onAddAttachments"
              ></v-file-input>
              <div v-if="form.attachments && form.attachments.length > 0" class="attachment-preview-container">
                <div
                  v-for="(file, i) in form.attachments"
                  :key="'new-att-'+i"
                  class="attachment-preview-card"
                >
                  <a :href="getLocalFileUrl(file)" target="_blank" class="preview-link">
                    <img
                      v-if="isLocalImage(file)"
                      :src="getLocalFileUrl(file)"
                      class="preview-img"
                      alt="preview"
                    />
                    <div v-else class="preview-file-placeholder">
                      <v-icon large color="grey darken-1">mdi-file-document-outline</v-icon>
                      <span class="preview-file-ext">{{ getFileExtension(file.name) }}</span>
                    </div>
                    <div class="preview-overlay">
                      <v-icon dark small>mdi-open-in-new</v-icon>
                    </div>
                  </a>
                  <span class="preview-filename text-truncate" :title="file.name">{{ file.name }}</span>
                  <v-btn
                    icon
                    x-small
                    class="preview-remove-btn"
                    color="red"
                    @click="removeNewAttachment(i)"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>
          <p class="subtitle-2 mb-2 grey--text text--darken-1">Customer Details <span class="caption">(optional)</span></p>
          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.customerName"
                :items="$store.state.customers.allCustomers.items || []"
                item-text="name"
                :filter="customerFilter"
                return-object
                label="Customer Name (Search or Type)"
                outlined dense clearable hide-details
                @change="onCustomerSelect"
              ></v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.customerPhone"
                :items="$store.state.customers.allCustomers.items || []"
                :filter="phoneFilter"
                :item-text="getPhoneDisplay"
                return-object
                label="Phone Number (Search or Type)"
                outlined dense clearable hide-details
                @change="onCustomerPhoneSelect"
              ></v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.customerEmail" label="Email" outlined dense hide-details></v-text-field>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showCreateDialog = false">Cancel</v-btn>
          <v-btn color="blue darken-3 white--text py-2" dark :loading="saving" @click="submitCreate">Create Inquiry</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== DETAIL / EDIT DIALOG ===== -->
    <v-dialog v-model="showDetailDialog" max-width="760px" persistent>
      <v-card v-if="selected">
        <v-card-title class="blue darken-3 white--text py-2">
          <v-icon dark left>mdi-comment-edit-outline</v-icon>
          Inquiry #{{ selectedSerial }} — {{ selected.title }}
          <v-spacer></v-spacer>
          <v-chip small dark :color="statusChipColor(selected.status)">{{ selected.status }}</v-chip>
        </v-card-title>

        <v-tabs v-model="detailTab" color="blue darken-3 white--text py-2">
          <v-tab>Details</v-tab>
          <v-tab>Activity Log ({{ selected.actions ? selected.actions.length : 0 }})</v-tab>
        </v-tabs>

        <v-tabs-items v-model="detailTab">
          <!-- TAB 1 - Edit Details -->
          <v-tab-item>
            <v-container class="pt-3">
              <v-alert v-if="editError" type="error" dense dismissible @input="editError = null">{{ editError }}</v-alert>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="editForm.title" label="Title / Subject *" outlined dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editForm.category" :items="categories" label="Category" outlined dense hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="6" v-if="editForm.category === 'Other'">
                  <v-text-field v-model="editForm.customCategory" label="Specify Category" outlined dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editForm.priority" :items="priorities" label="Priority" outlined dense hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editForm.status" :items="statuses" label="Status" outlined dense hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="6" v-if="editForm.category === 'Flight Booking' || editForm.category === 'Ticket Cancellation' || editForm.category === 'Ticket Refund'">
                  <v-text-field v-model="editForm.flightStartDate" label="Flight Start Date" type="date" outlined dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" v-if="editForm.category === 'Flight Booking' || editForm.category === 'Ticket Cancellation' || editForm.category === 'Ticket Refund'">
                  <v-text-field v-model="editForm.flightEndDate" label="Flight End Date" type="date" outlined dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="editForm.description" label="Description" outlined dense rows="3" hide-details></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    label="Add Attachment(s)"
                    outlined dense hide-details
                    prepend-icon="mdi-paperclip"
                    multiple
                    @change="onAddEditAttachments"
                  ></v-file-input>
                  <div v-if="editForm.attachments && editForm.attachments.length > 0" class="attachment-preview-container">
                    <div
                      v-for="(file, i) in editForm.attachments"
                      :key="'edit-att-'+i"
                      class="attachment-preview-card"
                    >
                      <a :href="getLocalFileUrl(file)" target="_blank" class="preview-link">
                        <img
                          v-if="isLocalImage(file)"
                          :src="getLocalFileUrl(file)"
                          class="preview-img"
                          alt="preview"
                        />
                        <div v-else class="preview-file-placeholder">
                          <v-icon large color="grey darken-1">mdi-file-document-outline</v-icon>
                          <span class="preview-file-ext">{{ getFileExtension(file.name) }}</span>
                        </div>
                        <div class="preview-overlay">
                          <v-icon dark small>mdi-open-in-new</v-icon>
                        </div>
                      </a>
                      <span class="preview-filename text-truncate" :title="file.name">{{ file.name }}</span>
                      <v-btn
                        icon
                        x-small
                        class="preview-remove-btn"
                        color="red"
                        @click="removeEditAttachment(i)"
                      >
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div v-if="editForm.remainingAttachments && editForm.remainingAttachments.length > 0" class="mt-3">
                    <p class="subtitle-2 mb-2 grey--text text--darken-1">Saved Attachments:</p>
                    <div class="attachment-preview-container">
                      <div
                        v-for="(path, i) in editForm.remainingAttachments"
                        :key="'saved-att-'+i"
                        class="attachment-preview-card"
                      >
                        <a :href="getAttachmentUrl(path)" target="_blank" class="preview-link">
                          <img
                            v-if="isSavedImage(path)"
                            :src="getAttachmentUrl(path)"
                            class="preview-img"
                            alt="preview"
                          />
                          <div v-else class="preview-file-placeholder">
                            <v-icon large color="grey darken-1">mdi-file-document-outline</v-icon>
                            <span class="preview-file-ext">{{ getFileExtension(path) }}</span>
                          </div>
                          <div class="preview-overlay">
                            <v-icon dark small>mdi-open-in-new</v-icon>
                          </div>
                        </a>
                        <span class="preview-filename text-truncate" :title="getFileName(path)">{{ getFileName(path) }}</span>
                        <v-btn
                          icon
                          x-small
                          class="preview-remove-btn"
                          color="red"
                          @click="removeSavedAttachment(i)"
                        >
                          <v-icon x-small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <p class="subtitle-2 mb-2 grey--text text--darken-1">Customer Details</p>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-combobox
                    v-model="editForm.customerName"
                    :items="$store.state.customers.allCustomers.items || []"
                    item-text="name"
                    :filter="customerFilter"
                    return-object
                    label="Customer Name (Search or Type)"
                    outlined dense clearable hide-details
                    @change="onEditCustomerSelect"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-combobox
                    v-model="editForm.customerPhone"
                    :items="$store.state.customers.allCustomers.items || []"
                    :filter="phoneFilter"
                    :item-text="getPhoneDisplay"
                    return-object
                    label="Phone (Search or Type)"
                    outlined dense clearable hide-details
                    @change="onEditCustomerPhoneSelect"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editForm.customerEmail" label="Email" outlined dense hide-details></v-text-field>
                </v-col>
              </v-row>
            </v-container>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text @click="showDetailDialog = false">Close</v-btn>
              <v-btn color="blue darken-3 white--text py-2" dark :loading="saving" @click="submitUpdate">Save Changes</v-btn>
            </v-card-actions>
          </v-tab-item>

          <!-- TAB 2 - Activity Log -->
          <v-tab-item>
            <v-container>
              <!-- Log new action -->
              <v-row align="center" class="mb-2">
                <v-col cols="9">
                  <v-text-field
                    v-model="newActionText"
                    label="Log an action (e.g. Called customer, Sent quote...)"
                    outlined dense hide-details
                    @keyup.enter="submitAction"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-btn color="blue darken-3 white--text py-2" dark block :loading="saving" @click="submitAction">
                    <v-icon left small>mdi-send</v-icon> Log
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider class="mb-3"></v-divider>

              <!-- Timeline -->
              <div v-if="selected.actions && selected.actions.length > 0">
                <div v-for="action in [...selected.actions].reverse()" :key="action.id" class="inq-action-item">
                  <div class="inq-action-dot"></div>
                  <div class="inq-action-body">
                    <div class="inq-action-text">{{ action.actionText }}</div>
                    <div class="inq-action-meta">
                      <v-icon x-small class="mr-1">mdi-account</v-icon>
                      {{ action.performedByUser ? action.performedByUser.name : 'Agent' }}
                      <v-icon x-small class="mx-1">mdi-clock-outline</v-icon>
                      {{ formatDate(action.createdAt) }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center pa-6 grey--text">
                <v-icon large color="grey lighten-2">mdi-timeline-outline</v-icon>
                <p class="mt-2">No actions logged yet.</p>
              </div>
            </v-container>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text @click="showDetailDialog = false">Close</v-btn>
            </v-card-actions>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="red darken-2 white--text">
          <v-icon dark left>mdi-delete-alert</v-icon> Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4" v-if="toDelete">
          Are you sure you want to delete inquiry <strong>#{{ toDeleteSerial }} — {{ toDelete.title }}</strong>? This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="saving" @click="executeDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </template><!-- end v-if canViewInquiries -->
  </div>
</template>

<script>
import config from '@/config'

export default {
  name: 'InquiriesPage',
  data: () => ({
    statusFilter: 'All',
    detailTab: 0,
    showCreateDialog: false,
    showDetailDialog: false,
    showDeleteDialog: false,
    selected: null,
    selectedSerial: null,
    toDelete: null,
    toDeleteSerial: null,
    newActionText: '',
    searchQuery: '',
    formError: null,
    editError: null,
    form: {
      title: '', description: '', category: 'Flight Booking', customCategory: '',
      priority: 'Medium', customerId: null, customerName: '', customerPhone: '', customerEmail: '', attachments: []
    },
    editForm: {
      id: null, title: '', description: '', category: '', customCategory: '',
      priority: 'Medium', status: 'Open', customerId: null,
      customerName: '', customerPhone: '', customerEmail: '', attachments: []
    },
    categories: [
      'Flight Booking', 'Ticket Cancellation', 'Ticket Refund',
      'Visa Inquiry', 'Baggage Query', 'Payment / Invoice',
      'Travel Insurance', 'Hotel / Accommodation','Umrah Package', 'General Support', 'Other'
    ],
    priorities: ['Low', 'Medium', 'High', 'Urgent'],
    statuses: ['Open', 'In Progress', 'Resolved', 'Closed'],
  }),

  computed: {
    canViewInquiries () { return this.$store.getters['account/canViewInquiries'] },
    allInquiries () { return this.$store.state.inquiries.inquiries },
    loading () { return this.$store.state.inquiries.loading },
    saving () { return this.$store.state.inquiries.saving },
    openCount () { return this.$store.getters['inquiries/openCount'] },
    filteredInquiries () {
      let list = this.statusFilter === 'All'
        ? this.allInquiries
        : this.allInquiries.filter(i => i.status === this.statusFilter)

      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        list = list.filter(i => {
          return (
            (i.title && i.title.toLowerCase().includes(query)) ||
            (i.customer && i.customer.name && i.customer.name.toLowerCase().includes(query)) ||
            (i.customerName && i.customerName.toLowerCase().includes(query)) ||
            (i.description && i.description.toLowerCase().includes(query)) ||
            (i.category && i.category.toLowerCase().includes(query)) ||
            (i.customCategory && i.customCategory.toLowerCase().includes(query)) ||
            (i.id && i.id.toString() === query)
          )
        })
      }

      // Sort newest first (highest id on top) so recent inquiries appear at the top
      return [...list].sort((a, b) => b.id - a.id)
    }
  },

  mounted () {
    this.loadInquiries()
    // Load customers for the autocomplete if not yet loaded
    if (!this.$store.state.customers.allCustomers.items || this.$store.state.customers.allCustomers.items.length === 0) {
      this.$store.dispatch('customers/getAll')
    }
  },

  methods: {
    loadInquiries () {
      this.$store.dispatch('inquiries/fetchAll')
    },

    countByStatus (status) {
      return this.allInquiries.filter(i => i.status === status).length
    },

    priorityColor (p) {
      return { Urgent: 'red darken-2', High: 'orange darken-2', Medium: 'blue darken-1', Low: 'grey darken-1' }[p] || 'grey'
    },

    statusChipColor (s) {
      return { Open: 'red', 'In Progress': 'orange darken-2', Resolved: 'green darken-1', Closed: 'grey darken-1' }[s] || 'grey'
    },

    getAttachmentUrl (path) {
      if (!path) return ''
      const baseUrl = config.apiUrl.replace(/\/$/, '')
      return `${baseUrl}/${path}`
    },

    formatDate (d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },

    // ---- Create ----
    parseAttachments(jsonStr) {
      if (!jsonStr) return [];
      try {
        return JSON.parse(jsonStr);
      } catch (e) {
        return [];
      }
    },

    customerFilter(item, queryText) {
      const name = item.name ? item.name.toLowerCase() : '';
      const phone = item.mobileNumber ? item.mobileNumber.toLowerCase() : (item.contactNumber ? item.contactNumber.toLowerCase() : '');
      const query = queryText.toLowerCase();
      return name.includes(query) || phone.includes(query);
    },

    phoneFilter(item, queryText) {
      const phone = item.mobileNumber ? item.mobileNumber.toLowerCase() : (item.contactNumber ? item.contactNumber.toLowerCase() : '');
      const name = item.name ? item.name.toLowerCase() : '';
      const query = queryText.toLowerCase();
      return phone.includes(query) || name.includes(query);
    },

    getPhoneDisplay(item) {
      if (!item || typeof item !== 'object') return item || '';
      return item.mobileNumber || item.contactNumber || item.name || '';
    },

    onCustomerPhoneSelect(val) {
      if (typeof val === 'object' && val !== null) {
        this.form.customerId = val.id;
        this.form.customerName = val.name;
        this.form.customerPhone = val.mobileNumber || val.contactNumber || '';
        this.form.customerEmail = val.email || '';
      } else {
        this.form.customerPhone = val || '';
      }
    },

    onEditCustomerPhoneSelect(val) {
      if (typeof val === 'object' && val !== null) {
        this.editForm.customerId = val.id;
        this.editForm.customerName = val.name;
        this.editForm.customerPhone = val.mobileNumber || val.contactNumber || '';
        this.editForm.customerEmail = val.email || '';
      } else {
        this.editForm.customerPhone = val || '';
      }
    },

    onAddAttachments (files) {
      if (!files) return;
      const list = Array.isArray(files) ? files : [files];
      list.forEach(f => { if (f) this.form.attachments.push(f); });
    },

    removeNewAttachment (index) {
      this.form.attachments.splice(index, 1);
    },

    onAddEditAttachments (files) {
      if (!files) return;
      const list = Array.isArray(files) ? files : [files];
      list.forEach(f => { if (f) this.editForm.attachments.push(f); });
    },

    removeEditAttachment (index) {
      this.editForm.attachments.splice(index, 1);
    },

    removeSavedAttachment (index) {
      this.editForm.remainingAttachments.splice(index, 1);
    },

    getLocalFileUrl (file) {
      if (!file) return '';
      return URL.createObjectURL(file);
    },

    isLocalImage (file) {
      if (!file) return false;
      if (file.type && file.type.startsWith('image/')) return true;
      return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(file.name);
    },

    isSavedImage (path) {
      if (!path) return false;
      return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(path);
    },

    getFileExtension (filename) {
      if (!filename) return '';
      const parts = filename.split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : '';
    },

    getFileName (path) {
      if (!path) return '';
      return path.substring(path.lastIndexOf('/') + 1);
    },

    openCreateDialog () {
      this.form = {
        title: '', description: '', category: 'Flight Booking', customCategory: '',
        priority: 'Medium', customerId: null, customerName: '', customerPhone: '', customerEmail: '',
        flightStartDate: '', flightEndDate: '', attachments: []
      }
      this.formError = null
      this.showCreateDialog = true
    },

    onCustomerSelect (val) {
      if (typeof val === 'object' && val !== null) {
        this.form.customerId = val.id
        this.form.customerName = val.name
        this.form.customerPhone = val.mobileNumber || val.contactNumber || ''
        this.form.customerEmail = val.email || ''
      } else {
        this.form.customerId = null
        this.form.customerName = val || ''
      }
    },

    onCategoryChange (val) {
      if (val === 'Umrah Package' && !this.form.description) {
        this.form.description = 'Flight: \nVisa: \nTransfer: \nZiarat: \nMakkah Hotel (how many nights): \nMadinah Hotel (how many nights): \nPassengers (Adult / Child / Youth): '
      }
    },

    onEditCustomerSelect (val) {
      if (typeof val === 'object' && val !== null) {
        this.editForm.customerId = val.id
        this.editForm.customerName = val.name
        this.editForm.customerPhone = val.mobileNumber || val.contactNumber || ''
        this.editForm.customerEmail = val.email || ''
      } else {
        this.editForm.customerId = null
        this.editForm.customerName = val || ''
      }
    },

    async submitCreate () {
      if (!this.form.title || !this.form.category) {
        this.formError = 'Title and category are required.'
        return
      }
      // Resolve phone in case it's still an object from combobox
      const payload = { ...this.form }
      if (typeof payload.customerPhone === 'object' && payload.customerPhone !== null) {
        payload.customerPhone = payload.customerPhone.mobileNumber || payload.customerPhone.contactNumber || ''
      }
      try {
        await this.$store.dispatch('inquiries/create', payload)
        this.showCreateDialog = false
      } catch (e) {
        this.formError = 'Failed to create inquiry. Please try again.'
      }
    },

    // ---- Detail / Edit ----
    openDetailDialog (inq, serial) {
      this.selected = inq
      this.selectedSerial = serial
      this.editForm = {
        id: inq.id,
        title: inq.title,
        description: inq.description,
        category: inq.category,
        customCategory: inq.customCategory || '',
        priority: inq.priority,
        status: inq.status,
        customerId: inq.customerId,
        customerName: inq.customerName || '',
        customerPhone: inq.customerPhone || '',
        customerEmail: inq.customerEmail || '',
        flightStartDate: inq.flightStartDate || '',
        flightEndDate: inq.flightEndDate || '',
        attachments: [],
        remainingAttachments: inq.attachments ? this.parseAttachments(inq.attachments) : (inq.attachmentPath ? [inq.attachmentPath] : [])
      }
      this.newActionText = ''
      this.editError = null
      this.detailTab = 0
      this.showDetailDialog = true
    },

    async submitUpdate () {
      if (!this.editForm.title) {
        this.editError = 'Title is required.'
        return
      }
      // Resolve phone in case it's still an object from combobox
      const payload = { ...this.editForm }
      if (typeof payload.customerPhone === 'object' && payload.customerPhone !== null) {
        payload.customerPhone = payload.customerPhone.mobileNumber || payload.customerPhone.contactNumber || ''
      }
      payload.remainingAttachments = JSON.stringify(this.editForm.remainingAttachments || [])
      try {
        const updated = await this.$store.dispatch('inquiries/update', payload)
        this.selected = updated
        this.editError = null
      } catch (e) {
        this.editError = 'Failed to save changes.'
      }
    },

    async submitAction () {
      if (!this.newActionText.trim()) return
      try {
        const updated = await this.$store.dispatch('inquiries/addAction', {
          inquiryId: this.selected.id,
          actionText: this.newActionText.trim()
        })
        this.selected = updated
        this.newActionText = ''
      } catch (e) {
        console.error('Failed to log action', e)
      }
    },

    // ---- Delete ----
    confirmDelete (inq, serial) {
      this.toDelete = inq
      this.toDeleteSerial = serial
      this.showDeleteDialog = true
    },

    async executeDelete () {
      if (!this.toDelete) return
      await this.$store.dispatch('inquiries/deleteInquiry', this.toDelete.id)
      this.showDeleteDialog = false
      this.toDelete = null
    }
  }
}
</script>

<style scoped>
.inquiries-page { padding: 16px; }

.inq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.inq-header-left { display: flex; align-items: center; }
.inq-title { font-size: 20px; font-weight: 600; color: #4527a0; }

.inq-filter-bar { margin-bottom: 12px; }

.inq-table-wrap {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}
.inq-row:hover { background-color: #cbcbcb !important; cursor: pointer; }

/* Activity log timeline */
.inq-action-item {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  align-items: flex-start;
}
.inq-action-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: #6a1b9a;
  margin-top: 5px;
  flex-shrink: 0;
}
.inq-action-body { flex: 1; }
.inq-action-text { font-size: 14px; color: #212121; }
.inq-action-meta { font-size: 11px; color: #9e9e9e; margin-top: 2px; }

/* Attachment Previews */
.attachment-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
}
.attachment-preview-card {
  position: relative;
  width: 90px;
  height: 90px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.attachment-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  border-color: #1976d2;
}
.preview-link {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  position: relative;
}
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.preview-file-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px;
}
.preview-file-ext {
  font-size: 10px;
  font-weight: 700;
  color: #757575;
  margin-top: 4px;
}
.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.preview-link:hover .preview-overlay {
  opacity: 1;
}
.preview-filename {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  color: #37474f;
  font-size: 9px;
  padding: 2px 6px;
  text-align: center;
  font-weight: 500;
  border-top: 1px solid #e0e0e0;
}
.preview-remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  z-index: 2;
}
.preview-remove-btn:hover {
  background-color: #ffebee !important;
}
</style>
